import React, { useState } from 'react';
import { Card, Button, Row, Col, Typography, Space, Form, Input, Select, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons';
import CustomList from '../../../../components/List';
import ListItem from '../../../../components/ListItem';
import CustomPagination from '../../../../components/CustomPagination';
import API from '../../../../../API';

const { Text, Title } = Typography;
const { TextArea } = Input;

const SubProjectsTab = ({
    projectId,
    subProjects,
    subProjectsPagination,
    subProjectsLoading,
    subProjectsPage,
    subProjectsPerPage,
    onPageChange,
    councils,
    packages,
    districts,
    subProjectTypes,
    subProjectStatuses,
    districtsLoading,
    subProjectTypesLoading,
    subProjectStatusesLoading,
    onRefresh,
    openSummaryModal
}) => {
    const [subProjectForm] = Form.useForm();
    const [editingSubProject, setEditingSubProject] = useState(null);
    const [showSubProjectModal, setShowSubProjectModal] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSubProjectEdit = (subProject) => {
        setEditingSubProject(subProject);

        let councilId = null;
        if (subProject?.procuring_entity_id) {
            const correspondingCouncil = councils?.find(council =>
                council.procuring_entities &&
                council.procuring_entities.some(pe => pe.id === subProject.procuring_entity_id)
            );
            if (correspondingCouncil) {
                councilId = correspondingCouncil.id;
            }
        }

        subProjectForm.setFieldsValue({
            name: subProject?.name || '',
            description: subProject?.description || '',
            code: subProject?.code || '',
            quantity: subProject?.quantity || '',
            district_id: subProject?.district_id || null,
            procuring_entity_package_id: subProject?.procuring_entity_package_id || null,
            council_id: councilId,
            sub_project_type_id: subProject?.sub_project_type_id || null,
            sub_project_status_id: subProject?.sub_project_status_id || null
        });
        setShowSubProjectModal(true);
    };

    const handleSubProjectSave = async () => {
        try {
            setSaving(true);
            const values = await subProjectForm.validateFields();

            const payload = {
                ...values,
                project_id: projectId
            };

            if (editingSubProject) {
                await API.updateSubProject(payload, editingSubProject.id);
                message.success('Sub-project updated successfully');
            } else {
                await API.createSubProject(payload);
                message.success('Sub-project created successfully');
            }

            setShowSubProjectModal(false);
            setEditingSubProject(null);
            subProjectForm.resetFields();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error saving sub-project:', error);
            message.error('Failed to save sub-project');
        } finally {
            setSaving(false);
        }
    };

    const subProjectHeader = [
        { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24, header: 'Name' },
        { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24, header: 'Description' },
        { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24, header: 'Actions' }
    ];

    const renderSubProjectItem = ({ item }) => (
        <ListItem
            key={item.id}
            name={item.name}
            item={item}
            isPackage={false}
        >
            <Row gutter={[8, 0]} style={{ width: '100%' }}>
                <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24} className="contentEllipse">
                    <div className="mobile-label">Name</div>
                    <Text strong>{item.name}</Text>
                </Col>
                <Col xxl={9} xl={9} lg={9} md={9} sm={24} xs={24} className="contentEllipse">
                    <div className="mobile-label">Description</div>
                    <Text ellipsis>{item.description || '—'}</Text>
                </Col>
                <Col xxl={4} xl={4} lg={4} md={4} sm={24} xs={24} className="contentEllipse">
                    <div className="mobile-label">Actions</div>
                    <Space>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleSubProjectEdit(item)}
                            size="large"
                            title="Edit"
                        />
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => openSummaryModal('sub_project', item?.name, item?.id)}
                            size="large"
                            title="Summary Settings"
                        />
                    </Space>
                </Col>
            </Row>
        </ListItem>
    );

    return (
        <>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>Sub-Projects</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingSubProject(null);
                            subProjectForm.resetFields();
                            setShowSubProjectModal(true);
                        }}
                    >
                        Add Sub-Project
                    </Button>
                </div>
                <CustomList
                    itemName="Sub-Projects"
                    items={subProjects}
                    page={subProjectsPage}
                    itemCount={subProjectsPagination?.total || (subProjects?.length || 0)}
                    loading={subProjectsLoading}
                    headerLayout={subProjectHeader}
                    renderListItem={renderSubProjectItem}
                    showToolbar={false}
                />
                
                {subProjectsPagination && subProjectsPagination.total > 0 && (
                    <CustomPagination 
                        pagination={subProjectsPagination}
                        onPageChange={onPageChange}
                        loading={subProjectsLoading}
                    />
                )}
            </Card>

            <Modal
                title={editingSubProject ? 'Edit Sub-Project' : 'Add Sub-Project'}
                open={showSubProjectModal}
                onOk={handleSubProjectSave}
                onCancel={() => {
                    setShowSubProjectModal(false);
                    setEditingSubProject(null);
                    subProjectForm.resetFields();
                }}
                confirmLoading={saving}
                width={800}
            >
                <Form form={subProjectForm} layout="vertical">
                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="name"
                                label="Sub-Project Name"
                                rules={[{ required: true, message: 'Sub-project name is required' }]}
                            >
                                <Input placeholder="Enter sub-project name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="code"
                                label="Sub-Project Code"
                            >
                                <Input placeholder="Enter sub-project code" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} placeholder="Enter description" />
                    </Form.Item>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="quantity"
                                label="Quantity"
                            >
                                <Input type="number" placeholder="Enter quantity" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="district_id"
                                label="District"
                                rules={[{ required: true, message: 'District is required' }]}
                            >
                                <Select
                                    placeholder="Select district"
                                    loading={districtsLoading}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {districts?.map(district => (
                                        <Select.Option key={district.id} value={district.id}>
                                            {district.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="council_id"
                                label="Council"
                            >
                                <Select
                                    placeholder="Select council"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {councils?.map(council => (
                                        <Select.Option key={council.id} value={council.id}>
                                            {council.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="procuring_entity_package_id"
                                label="Package"
                            >
                                <Select
                                    placeholder="Select package"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {packages?.map(pkg => (
                                        <Select.Option key={pkg.id} value={pkg.id}>
                                            {pkg.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="sub_project_type_id"
                                label="Sub-Project Type"
                            >
                                <Select
                                    placeholder="Select type"
                                    loading={subProjectTypesLoading}
                                >
                                    {subProjectTypes?.map(type => (
                                        <Select.Option key={type.id} value={type.id}>
                                            {type.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="sub_project_status_id"
                                label="Status"
                            >
                                <Select
                                    placeholder="Select status"
                                    loading={subProjectStatusesLoading}
                                >
                                    {subProjectStatuses?.map(status => (
                                        <Select.Option key={status.id} value={status.id}>
                                            {status.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default SubProjectsTab;

